import React, { useState } from "react";
import {
  CheckSquare,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  QrCode,
  Search,
  SlidersHorizontal,
} from "lucide-react";
import CustomDatePicker from "../../components/CustomDatePicker";
import MobileFilterSheet from "../../components/MobileFilterSheet";
import AppInput from "../../components/ui/AppInput";
import { DESIGN_TOKENS } from "../../constants/designTokens";
import {
  MOBILE_HEADER_CONTAINER_CLASS,
  MOBILE_HEADER_CONTAINER_SCROLLED_CLASS,
  MOBILE_HEADER_COUNT_BADGE_CLASS,
  MOBILE_HEADER_COUNT_HIGHLIGHT_CLASS,
  MOBILE_HEADER_COUNT_TEXT_CLASS,
  MOBILE_HEADER_SEARCH_ICON_CLASS,
  MOBILE_HEADER_SEARCH_WRAPPER_CLASS,
  MOBILE_HEADER_SECOND_ROW_CLASS,
  MOBILE_HEADER_SORT_GROUP_CLASS,
  MOBILE_HEADER_SORT_LABEL_CLASS,
  MOBILE_HEADER_TOP_ROW_CLASS,
} from "../common/mobileHeaderTokens";
import {
  ATTENDANCE_MOBILE_HEADER_NAV_BUTTON_CLASS,
  ATTENDANCE_MOBILE_HEADER_SEARCH_INPUT_CLASS,
  getAttendanceBulkThemeBgClass,
  getAttendanceMobileBulkStatusButtonClass,
} from "./attendanceMobileStyles";

export default function AttendanceMobileFixedHeader({
  activeTab,
  bulkHoverState,
  changeDate,
  changeMonth,
  changeYear,
  currentDate,
  currentMonth,
  currentYear,
  dailyStats,
  filteredCount,
  handleBulkStatusChange,
  handleClearSelection,
  handleSaveDaily,
  handleSelectAll,
  handleSyncSoteria,
  isSoteriaLoading,
  selectedIds,
  setCurrentDate,
  setCurrentMonth,
  setCurrentYear,
  setSearchQuery,
  setSortKey,
  setSortOrder,
  sortKey,
  sortOrder,
  searchQuery,
  targetGroup,
  theme,
  isScrolled,
}) {
  const brand = DESIGN_TOKENS.brand;
  const [isFilterSheetOpen, setIsFilterSheetOpen] = useState(false);
  const activeCount = selectedIds.length;
  const sortOptions = targetGroup === "students"
    ? [
      { value: "grade", label: "학년순" },
      { value: "class", label: "반별순" },
      { value: "name", label: "이름순" },
      { value: "status", label: "상태순" },
      { value: "department", label: "부서순" },
    ]
    : [
      { value: "responsibility", label: "담당순" },
      { value: "role", label: "직분순" },
      { value: "name", label: "이름순" },
      { value: "status", label: "상태순" },
      { value: "department", label: "부서순" },
    ];

  return (
    <div className="flex-shrink-0">
      <div className={isScrolled ? MOBILE_HEADER_CONTAINER_SCROLLED_CLASS : MOBILE_HEADER_CONTAINER_CLASS}>
        <div className={MOBILE_HEADER_TOP_ROW_CLASS}>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <button onClick={() => activeTab === "monthly" ? changeMonth(-1) : activeTab === "yearly" ? changeYear(-1) : changeDate(-7)} className={ATTENDANCE_MOBILE_HEADER_NAV_BUTTON_CLASS}><ChevronLeft size={16} /></button>
            <div className="relative z-50">
              <CustomDatePicker
                theme={theme}
                type={activeTab === "monthly" ? "month" : activeTab === "yearly" ? "year" : "date"}
                value={activeTab === "monthly" ? currentMonth : activeTab === "yearly" ? currentYear : currentDate}
                onChange={activeTab === "monthly" ? setCurrentMonth : activeTab === "yearly" ? setCurrentYear : setCurrentDate}
                customTrigger={
                  <span className={`text-xs font-black ${brand.primaryTextDarkClass} font-do-hyeon cursor-pointer whitespace-nowrap`}>
                    {activeTab === "monthly" ? `${currentMonth.split("-")[1]}월` : activeTab === "yearly" ? `${currentYear}년` : currentDate.slice(5).replace("-", ".")}
                  </span>
                }
              />
            </div>
            <button onClick={() => activeTab === "monthly" ? changeMonth(1) : activeTab === "yearly" ? changeYear(1) : changeDate(7)} className={ATTENDANCE_MOBILE_HEADER_NAV_BUTTON_CLASS}><ChevronRight size={16} /></button>
          </div>

          <div className="flex items-center gap-1">
            <div className={MOBILE_HEADER_COUNT_BADGE_CLASS}>
              <span className={MOBILE_HEADER_COUNT_TEXT_CLASS}>
                {activeTab === "daily" ? (
                  <>
                    참석 <span className={MOBILE_HEADER_COUNT_HIGHLIGHT_CLASS}>{dailyStats.present + dailyStats.late}</span>명
                  </>
                ) : (
                  <>
                    총 <span className={MOBILE_HEADER_COUNT_HIGHLIGHT_CLASS}>{filteredCount}</span>명
                  </>
                )}
              </span>
            </div>
          </div>
        </div>

        <div className={MOBILE_HEADER_SECOND_ROW_CLASS}>
          <div className="flex-1 min-w-0">
            <AppInput
              id="attendance-mobile-search"
              name="attendance_mobile_search"
              placeholder="검색"
              className={ATTENDANCE_MOBILE_HEADER_SEARCH_INPUT_CLASS}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onClear={() => setSearchQuery("")}
              icon={Search}
            />
          </div>

          <div className="flex items-center gap-1.5 flex-shrink-0">
            <button
              onClick={() => setIsFilterSheetOpen(true)}
              className="h-9 w-9 bg-white border border-gray-200 rounded-xl text-gray-600 shadow-sm flex items-center justify-center active:scale-95 transition"
              title="필터"
            >
              <SlidersHorizontal size={14} />
            </button>
          </div>
        </div>

        {activeTab === "daily" && (
          <div className="mt-1.5 pt-1.5 border-t border-gray-100">
            <div className="px-2.5 pb-1.5 flex items-center gap-1.5 justify-between">
              <div className="flex items-center gap-2">
                <div className="flex items-baseline gap-1 flex-shrink-0">
                  <span className={`text-xl font-black ${theme.text} font-do-hyeon`}>
                    {Math.round(((dailyStats.present + dailyStats.late) / (filteredCount || 1)) * 100)}%
                  </span>
                </div>
                <div className="w-24 h-2 bg-gray-100 rounded-full flex overflow-hidden shadow-inner border border-gray-200/50">
                  <div style={{ width: `${(dailyStats.present / (filteredCount || 1)) * 100}%` }} className="h-full bg-emerald-500"></div>
                  <div style={{ width: `${(dailyStats.late / (filteredCount || 1)) * 100}%` }} className="h-full bg-amber-400"></div>
                  <div style={{ width: `${(dailyStats.absent / (filteredCount || 1)) * 100}%` }} className="h-full bg-rose-500"></div>
                  <div style={{ width: `${(dailyStats.none / (filteredCount || 1)) * 100}%` }} className="h-full bg-gray-200"></div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <MobileFilterSheet
        isOpen={isFilterSheetOpen}
        onClose={() => setIsFilterSheetOpen(false)}
        title="출석부 필터"
        sortValue={sortKey}
        sortOptions={sortOptions}
        onChangeSort={setSortKey}
        sortOrder={sortOrder}
        onChangeSortOrder={setSortOrder}
        themeColor={theme?.primaryHex || "#354F38"}
      />
    </div>
  );
}
